<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isProcessing } from 'dashboard/composables/arrowsAi/documentMapper';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  document: { type: Object, required: true },
  selected: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'delete', 'download', 'edit']);
const { t } = useI18n();
const processing = computed(() => isProcessing(props.document));

const formatSize = size => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<template>
  <CardLayout selectable class="relative">
    <div class="absolute top-7 ltr:left-3 rtl:right-3">
      <Checkbox
        :model-value="selected"
        @update:model-value="emit('select', document.id)"
      />
    </div>
    <div class="flex items-start justify-between w-full gap-3">
      <div class="min-w-0">
        <h3 class="text-base text-n-slate-12 truncate">{{ document.name }}</h3>
        <p class="mt-1 text-sm text-n-slate-11 truncate">
          {{
            t('ARROWS_AI.DOCUMENTS.META', {
              fileName: document.fileName,
              size: formatSize(document.size),
            })
          }}
        </p>
        <p
          v-if="document.status === 'failed'"
          class="mt-1 text-xs text-n-ruby-9"
        >
          {{ document.errorMessage || t('ARROWS_AI.DOCUMENTS.FAILED') }}
        </p>
        <p v-else-if="processing" class="mt-1 text-xs text-n-slate-10">
          {{ t('ARROWS_AI.DOCUMENTS.PROCESSING') }}
        </p>
      </div>
      <div class="flex shrink-0 items-center">
        <Button
          :label="t('ARROWS_AI.DOCUMENTS.DOWNLOAD')"
          color="slate"
          variant="ghost"
          size="sm"
          icon="i-lucide-download"
          @click="emit('download', document)"
        />
        <Button
          v-if="document.editable"
          :label="t('ARROWS_AI.DOCUMENTS.EDIT')"
          color="slate"
          variant="ghost"
          size="sm"
          icon="i-lucide-pencil"
          :disabled="processing"
          @click="emit('edit', document)"
        />
        <Button
          :label="t('ARROWS_AI.DOCUMENTS.DELETE')"
          color="ruby"
          variant="ghost"
          size="sm"
          icon="i-lucide-trash"
          @click="emit('delete', document.id)"
        />
      </div>
    </div>
  </CardLayout>
</template>
